/**
 * MENU SYSTEM OVERVIEW — (notes for future Michael)
 * -------------------------------------------------
 * Public nav comes from settings/Header (TextObjects named "Menu ## ...").
 * Some menus (ex: Admin) point to settings/adminMenus to build submenus.
 *
 * Dynamic submenu items:
 * - each item = a Firestore content record
 * - option.src = that record's data (pure content)
 * - option.cfg = menu config values (CMS meta rules)
 *
 * URL pattern matches Svelte static + Firebase Hosting:
 *   /{lang}/{area}/{path}/{endpoint}/?query...
 * ex: /en/us/admin/roles/?col=roles
 *
 * Hide logic:
 * - UI hides admin links unless userRoles.admin === true
 * - Firestore rules also enforce access (client hide is convenience only)
 *
 * Why src + cfg instead of flatten:
 * - avoids field collisions
 * - keeps content and menu rule layers separate
 * - supports future admin pages (submissions, assets, jobs, etc.)
 *
 * First written w/ AI assist (2025-11-02). You own this. Evolve as needed.
 */

// getMenuAry.js
// PURPOSE:
// Build your navigation menu, localized by language + area
// Also supports dynamic submenus based on Firestore content.
// (Example: menu of staff attorneys, menu of practice areas, etc.)

import { translate } from './translate';
import { operators } from './operators';
import { getObjectField, slugify } from "cando-util";
import { isKeyValueTrue } from './isKeyValueTrue.svelte';
import { getLangHref } from './getLangHref.js';

const baseClass = (cfg, data) => cfg?.baseClass?.value || data?.content?.settings?.['Header']?.classes?.value?.menuBaseClass?.value || '';
const selectedClass = (cfg, data) => cfg?.selectedClass?.value || data?.content?.settings?.['Header']?.classes?.value?.menuSelectedClass?.value || '';

// --------------------------------------------------------------------
// MAIN FUNCTION — returned menu array for UI
// --------------------------------------------------------------------
export const getMenuAry = (menus, lang, area, data, store) => {
	// Pull top menu items
	const items = extractMenuDefs(menus, lang, area, data, store)
		.map(item => ({
			...item,
			// If this menu has dynamic dropdown options, build them
			options: buildFilterOptions(item, data, lang, area, store)
		}))
		.sort((a, b) => a.key.localeCompare(b.key))
		.filter(i => !i.hide); // drop hidden

	// Failsafe — at least one thing to show
	if (!items.length) {
		items.push({ label: 'Admin', href: '/admin/' });
	}

	return items;
};

// --------------------------------------------------------------------
// INTERNAL HELPERS — ONLY USED BY THIS FILE
// We keep them here instead of spreading files all over the project.
// --------------------------------------------------------------------

// Find all "Menu blah" text objects in your CMS data
// These are the top-level menu buttons
const extractMenuDefs = (menus, lang, area, data, store) =>
	Object.keys(menus)
		.filter(k => menus[k].type === 'TextObject' && k.startsWith('Menu '))
		.map(key => {
			const v = menus[key]?.value || {};
			const href = getLangHref(v?.href?.value, lang, area);

			return {
				key,
				label: translate(v.label, lang).split('\n')[0], // first line only
				hide: isKeyValueTrue(v, store, 'hide.value'),
				persistSearch: isKeyValueTrue(v, store, 'persistSearch.value'),
				labelIcon: JSON.parse(
					menus?.[v?.labelIcon?.value]?.value?.icon || '{}'
				),
				href,
				class: data?.url?.pathname === href ? selectedClass(v, data) : baseClass(v, data),
				config: v // storing config so we can pull submenu info later
			};
		});

// If menu item has “options” setup, generate submenu items from Firestore content
// This is where attorney lists, practice area lists, etc come from
const buildFilterOptions = (item, data, lang, area, store) => {
	const cfg = item.config;

	// If menu isn’t configured for dynamic submenu, skip
	// At some point there could be a need to allow static values for options but not now.
	if (!(cfg.optionHref && cfg.optionLabel && cfg.options)) return null;

	// Example: cfg.options = "content, attorneys"
	// these values anticipate the use of just the ", " delimiter - not the +|+ or +||+ for two dimensional opStr's
	const [col, docId] = (cfg.options.value || '').split(', ');
	const [field, opStr, val] = cfg.opStr?.value ? cfg.opStr.value.split(', ') : [];
	const fieldParts = field ? field.split('.') : [];

	const labelFlds = (cfg.optionLabel.value || '').split(', ');
	const hrefFlds = (cfg.optionHref.value || '').split(', ').map(e => e.split('.'));
	const queryFlds = (cfg.optionQuery?.value || '').split(', ').map(e => e.split('.'));

	const source = data?.content?.[col]?.[docId] || data?.content?.[col] || {};
	const operator = opStr ? operators.find(p => p.firestore === opStr) : undefined;
	const operatorFn = operator?.javascript;

	const keys = Object.keys(source).sort();

	const filteredKeys = keys
		.filter(k =>
			!operatorFn || operatorFn(getObjectField({ src: source[k], cfg }, fieldParts), val)
		);

	const optionsAry = [];
	for (const k of filteredKeys) {
		const opt = {
			src: { ...source[k] },
			// ...source[k].value,
			col,
			docId,
			key: k,
			endpoint: source[k].endpoint === undefined ? k : source[k].endpoint.value,
			hrefFlds,
			queryFlds,
			labelFlds,
			currentLang: lang,
			cfg
		};

		const label = labelFlds
			.map(f => translate(getObjectField(opt, f.split('.')), lang))
			.join(' ');

		const href = getLangHref(computeHref(opt), lang, area);

		optionsAry.push({
			opt, label, href
		});
	}

	const mappedOptionsAry = [];
	for (const obj of optionsAry) {
		mappedOptionsAry.push({
			label: obj.label,
			href: obj.href,
			class: data?.url?.pathname === obj.href ? selectedClass(cfg, data) : baseClass(cfg, data),
			persistSearch: isKeyValueTrue(obj.opt, store, 'persistSearch.value'),
			option: obj.opt
		});
	}

	return mappedOptionsAry
		.filter(o => !o.hide);
};

// Wraps all href logic.
// - collect pieces
// - remove defaults
// - add query params
const computeHref = (option) => {
	const mapped = mapHrefFields(option);
	let href = buildHref(mapped);

	const q = buildQuery(option);
	if (q) href += `?${q}`;

	return href;
};

// Takes fields like ['category.name', 'lang'] and returns values to build a URL.
// If a field refers to language/area/link, we don't slugify it —
// otherwise we slugify to make URL-safe text.
const mapHrefFields = (option) =>
	option.hrefFlds.map(fieldPath => {
		const context = { ...option }; // lookup source
		const raw = getObjectField(context, fieldPath);

		if (!raw) return '';

		return raw;
	})
		.filter(Boolean);

// Turn `["criminal","dui"]` into `/criminal/dui/`
// Also strip default lang/area so we don't end up with `/en/sandiego/...`
const buildHref = (parts) => {
	// If nothing left, root menu item → homepage
	if (!parts.length) return '/';

	const joined = parts.join('/');

	// If it looks like an absolute URL, leave it alone
	if (joined.startsWith('http')) return joined;

	return `/${joined}/`;
};

// Build query string like ?foo=bar&x=1
const buildQuery = (option) =>
	option.queryFlds
		?.map(f => getObjectField({ ...option }, f))
		.filter(Boolean)
		.join('&') || '';
